import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import {
  setIsAlmostDone,
  setIsUserNotConfirmed,
  setShowSignupModal,
  setShowLoginModal,
  setUnverifiedEmail,
  setIsResendEmailSubmitted,
  setShowResetModalByDefault,
  setLoadResetPasswordComponent,
  setResetPasswordSuccess,
} from '../../slices/accounts';
import messages from './messages';
import styles from './LoginModal.module.scss';
import AuthService from '../../services/cognito/auth';
import FlyoutModal from '../common/FlyoutModal';
import ToastBanner from '../common/ToastBanner';
import LoginForm from '../forms/LoginForm';
import ErrorBanner from '../common/ErrorBanner';
import LinkButton from '../common/LinkButton';
import { getLastURIComponent } from '../../utils';

const LoginModal = ({
  isVisible,
  hideCreateAccount,
  setLoggedInCustomerId,
  mergeCart,
  redirectToAccountDashboard,
  showToastBanner,
}) => {
  const { formatMessage } = useIntl();
  const [errorMessage, setErrorMessage] = useState(null);
  const [isResend, setIsResend] = useState(false);
  const isAlmostDone = useSelector((state) => state.accounts.isAlmostDone);
  const [
    resetPasswordToastBannerToastBanner,
    setResetPasswordToastBannerToastBanner,
  ] = useState(showToastBanner);
  const isUserNotConfirmed = useSelector(
    (state) => state.accounts.isUserNotConfirmed,
  );
  const {
    unverifiedEmail,
    isResendEmailSubmitted,
    showResetModalByDefault,
    showLoginModal,
    resetPasswordSuccess,
  } = useSelector(
    (state) => state.accounts,
  );

  const dispatch = useDispatch();

  const closeModal = () => {
    if (isResendEmailSubmitted && !resetPasswordSuccess) {
      dispatch(setIsResendEmailSubmitted({ isResendEmailSubmitted: false }));
    } else if (showResetModalByDefault && showLoginModal) {
      dispatch(setShowResetModalByDefault({ showResetModalByDefault: false }));
      dispatch(setLoadResetPasswordComponent({ loadResetPasswordComponent: false }));
    } else {
      dispatch(setShowLoginModal({ showLoginModal: false }));
      dispatch(setShowSignupModal({ showSignupModal: false }));
      dispatch(setIsUserNotConfirmed({ isUserNotConfirmed: false }));
      dispatch(setIsAlmostDone({ isAlmostDone: false }));
      dispatch(setShowResetModalByDefault({ showResetModalByDefault: false }));
      dispatch(setLoadResetPasswordComponent({ loadResetPasswordComponent: false }));
      dispatch(setResetPasswordSuccess({ resetPasswordSuccess: false }));
      const lastURIComponent = getLastURIComponent().split('?').shift();
      if (lastURIComponent !== 'order-confirmation') {
        dispatch(setUnverifiedEmail({ unverifiedEmail: '' }));
      }
    }
  };
  const openSignUpModal = () => {
    dispatch(setShowSignupModal({ showSignupModal: true }));
    dispatch(setShowLoginModal({ showLoginModal: false }));
  };
  const handleError = (error, emailAddress) => {
    if (error.name === 'UserNotConfirmedException') {
      dispatch(setUnverifiedEmail({ unverifiedEmail: emailAddress }));
      dispatch(setIsUserNotConfirmed({ isUserNotConfirmed: true }));
      dispatch(setIsAlmostDone({ isAlmostDone: false }));
      setErrorMessage('');
    } else if (error.name) {
      dispatch(setIsUserNotConfirmed({ isUserNotConfirmed: false }));
      dispatch(setIsAlmostDone({ isAlmostDone: false }));
      setErrorMessage(error.message);
    } else {
      dispatch(setIsUserNotConfirmed({ isUserNotConfirmed: false }));
      dispatch(setIsAlmostDone({ isAlmostDone: false }));
      setErrorMessage(formatMessage(messages.notAuthorized));
    }
  };

  const resendVerificationEmail = async (username) => {
    try {
      const response = await AuthService.resendConfirmationCode({ username });
      if (response?.CodeDeliveryDetails?.Destination) {
        dispatch(setIsUserNotConfirmed({ isUserNotConfirmed: false }));
        dispatch(setIsAlmostDone({ isAlmostDone: true }));
        setIsResend(true);
      }
    } catch (error) {
      handleError(error, username);
    }
  };

  const closeToastBanner = () => {
    if (isAlmostDone && isResend) {
      dispatch(setIsAlmostDone({ isAlmostDone: false }));
      setIsResend(false);
    }
    if (isUserNotConfirmed) {
      dispatch(setIsUserNotConfirmed({ isUserNotConfirmed: false }));
    }
  };

  useEffect(() => {
    if (resetPasswordToastBannerToastBanner) {
      dispatch(setShowResetModalByDefault({ showResetModalByDefault: false }));
      dispatch(setShowLoginModal({ showLoginModal: true }));
      dispatch(setLoadResetPasswordComponent({ loadResetPasswordComponent: false }));
    }
  }, [dispatch, resetPasswordToastBannerToastBanner]);

  const closeResetPasswordToastBannerToastBanner = () => {
    setResetPasswordToastBannerToastBanner(false);
  };

  return (
    <FlyoutModal
      isVisible={isVisible}
      backButtonLabel={formatMessage(messages.backBtn)}
      onCloseModal={closeModal}
    >
      {(resetPasswordToastBannerToastBanner || resetPasswordSuccess) && (
        <ToastBanner
          type="warning"
          onClickCloseButton={closeResetPasswordToastBannerToastBanner}
        >
          <div>{formatMessage(messages.resetPasswordSuccess)}</div>
        </ToastBanner>
      )}
      <div>
        {isAlmostDone && (
          <h2 className={styles.loginHeader}>{ formatMessage(messages.almostDone) }</h2>
        )}
        {!isAlmostDone && !showResetModalByDefault && (
          <h2 className={styles.loginHeader}>
            {formatMessage(messages.signIn)}
          </h2>
        )}
        {!!errorMessage && (
          <ErrorBanner
            showError
            onCloseErrorMessage={() => setErrorMessage('')}
          >
            {errorMessage}
          </ErrorBanner>
        )}
        {isAlmostDone && !isUserNotConfirmed && !isResend && (
          <div className={styles.accountCreatedMessage}>
            {formatMessage(messages.verifyMessagePt1)}
            <span className={styles.unverifiedEmail}>{unverifiedEmail}</span>
            {`. ${formatMessage(messages.verifyMessagePt2)}`}
            <span className={styles.resendClickHereFirst}>
              <LinkButton
                id="resendButton"
                isBold
                onClick={() => resendVerificationEmail(unverifiedEmail)}
                linkText={formatMessage(messages.verifyMessagePt3)}
              />
            </span>
            {formatMessage(messages.verifyMessagePt4)}
          </div>
        )}
        {!isAlmostDone && isUserNotConfirmed && (
          <ToastBanner
            type="error"
            title={formatMessage(messages.verificationNeededTitle)}
            onClickCloseButton={closeToastBanner}
          >
            <div>
              {formatMessage(messages.verifyMessagePt1)}
              <span className={styles.unverifiedEmail}>{unverifiedEmail}</span>
              {`. ${formatMessage(messages.verifyMessagePt2)}`}
              <span className={styles.resendClickHere}>
                <LinkButton
                  id="resendButton"
                  isError
                  isBold
                  onClick={() => resendVerificationEmail(unverifiedEmail)}
                  linkText={formatMessage(messages.verifyMessagePt3)}
                />
              </span>
              {formatMessage(messages.verifyMessagePt4)}
            </div>
          </ToastBanner>
        )}
        {isAlmostDone && !isUserNotConfirmed && isResend && (
          <ToastBanner type="warning" onClickCloseButton={closeToastBanner}>
            <div>
              {formatMessage(messages.resendVerifyMessagePt1)}
              <span className={styles.unverifiedEmail}>{unverifiedEmail}</span>
              {`. ${formatMessage(messages.resendVerifyMessagePt2)} `}
            </div>
          </ToastBanner>
        )}
        <LoginForm
          loginErrorCallback={handleError}
          closeModalCallback={closeModal}
          setLoggedInCustomerId={setLoggedInCustomerId}
          mergeCart={mergeCart}
          redirectToAccountDashboard={redirectToAccountDashboard}
        />
        {(isAlmostDone || isUserNotConfirmed) ? (
          <p className={styles.verificationResendLink}>
            <span className={styles.verificationResendText}>
              {formatMessage(messages.verifyPrompt)}
            </span>
            <LinkButton
              id="resendButton"
              onClick={() => resendVerificationEmail(unverifiedEmail)}
              linkText={formatMessage(messages.resendLink)}
            />
          </p>
        ) : (
          !hideCreateAccount && (
            <p className={styles.signupLink}>
              <span
                className={styles.signupLinkText}
              >
                {formatMessage(messages.createAccountPrompt)}
              </span>
              <LinkButton
                id="signUpButton"
                onClick={openSignUpModal}
                linkText={formatMessage(messages.createAccountLink)}
              />
            </p>
          )
        )}
      </div>
    </FlyoutModal>
  );
};

LoginModal.propTypes = {
  isVisible: PropTypes.bool,
  hideCreateAccount: PropTypes.bool,
  setLoggedInCustomerId: PropTypes.func,
  mergeCart: PropTypes.bool,
  redirectToAccountDashboard: PropTypes.bool,
  showToastBanner: PropTypes.bool,
};
LoginModal.defaultProps = {
  isVisible: true,
  hideCreateAccount: false,
  mergeCart: false,
  redirectToAccountDashboard: true,
  showToastBanner: false,
  setLoggedInCustomerId: () => {},
};

export default LoginModal;
